import { Skeleton } from "@/components/ui/Skeleton";
import type { ServiceAreaCheckResult } from "./types";

// Inline status panel under DeliveryAddressInput. Brand voice from
// [00-foundations/brand-identity.md]: imperative, no exclamation marks,
// no apology theatre — "Outside our delivery area" not "Oh no!".
// The three negative branches each surface a discriminable affordance:
//   - out_of_area: "Notify me" mailto (no signup yet, kept simple)
//   - ambiguous: prompt to add city/state to disambiguate
//   - outside_us: hard stop (contract §20)
//   - geocoder_unavailable: try-again hint
export interface ServiceAreaResultProps {
  status: "idle" | "loading" | "success" | "error";
  data: ServiceAreaCheckResult | undefined;
  error: Error | null;
}

const NOTIFY_MAILTO = "mailto:hello@jdr.example?subject=Service%20area%20expansion%20request";

export function ServiceAreaResult({ status, data, error }: ServiceAreaResultProps): JSX.Element | null {
  if (status === "idle") return null;

  if (status === "loading") {
    return (
      <div className="mt-2 flex items-center gap-2 text-sm text-zinc-600" role="status" aria-live="polite">
        <Skeleton className="h-4 w-4" />
        <span>Checking address…</span>
      </div>
    );
  }

  if (status === "error") {
    return (
      <p className="mt-2 text-sm text-danger" role="alert">
        Couldn&rsquo;t check your address. Please try again. (Error: {error?.message ?? "unknown"})
      </p>
    );
  }

  if (!data) return null;

  if (data.serviceable) {
    const closest = data.eligible_yards[0];
    return (
      <p className="mt-2 text-sm text-emerald-900" role="status" aria-live="polite">
        <span aria-hidden="true">✓ </span>
        <strong>We deliver here.</strong>
        {closest ? (
          <>
            {" "}Drive distance: <strong>{Math.round(closest.drive_distance_miles)} mi</strong>.
          </>
        ) : null}
      </p>
    );
  }

  switch (data.reason) {
    case "out_of_area":
      // Phase 4c tax-and-NV-scope FR-7 — the customer-facing copy
      // names Colorado as the current scope so an NV-state resident
      // gets a concrete "coming soon" hint rather than a generic
      // rejection. The keystroke service-area check is the v1
      // user-facing rejection path; the cart-step `validate_address`
      // endpoint exists as a server-side defense gate.
      return (
        <div className="mt-2 text-sm text-zinc-700" role="status">
          <p>
            <span aria-hidden="true">✗ </span>
            <strong>We don&rsquo;t currently serve this address.</strong>
          </p>
          <p className="mt-1">
            JDR is launching with Colorado service only. We&rsquo;ll expand from there.
          </p>
          <a className="mt-1 inline-block text-brand-red underline" href={NOTIFY_MAILTO}>
            Notify me when you serve my area →
          </a>
        </div>
      );
    case "ambiguous_address":
      return (
        <p className="mt-2 text-sm text-zinc-700" role="status">
          <strong>We couldn&rsquo;t pinpoint that address.</strong> Try adding the city and state.
        </p>
      );
    case "outside_us":
      return (
        <p className="mt-2 text-sm text-zinc-700" role="status">
          <strong>We deliver in the United States only.</strong>
        </p>
      );
    case "geocoder_unavailable":
      return (
        <p className="mt-2 text-sm text-zinc-700" role="status">
          Address lookup is having a moment. Please try again in a moment.
        </p>
      );
    case "missing_address":
      return null;
    default:
      return null;
  }
}
